/**
 * galaxy_map.ts — the GALACTIC MAP (G in space): the four-arm spiral as an
 * orbit-camera chart you can fly with the mouse — wheel zooms on the cursor
 * from the whole galaxy down to a SINGLE STAR SYSTEM (past ~×40 a system's
 * orbit rings and planets resolve around its star, so a charted user world
 * — every public world is its own system — can be found among hundreds),
 * drag orbits, right-drag pans, double-click focuses. Detail layers: arm
 * dust + core bulge, star glow by class, sovereignty tints, incursion
 * halos, wormholes, the active course, distance rings from HOME in
 * light-hours (with the manual-course days), labels that thin themselves
 * by zoom, a hover card and a searchable system list. Click a star for the
 * travel panel: HYPER-WARP (a tunnel jump, seconds) or SET COURSE (manual
 * interstellar — real DAYS; it runs on the wall clock and survives logout).
 *
 * Pure presentation like space_map: SpaceModule feeds update() and receives
 * actions through the constructor callbacks.
 */
import { type StarSystem, galacticDistance, COURSE_S_PER_LH, systemSector } from './galaxy.js';
import { PLANETS, SECTOR_EDGE_M, worldPlanetOf } from './space_sector.js';
import { ChartView, niceStep, hash01 } from './chart_view.js';
import { naturalOn } from './space/natural/natural_controller.js';
import { systemMakeup, systemStarLabel } from './space/natural/system_makeup.js';
import { starChartHex } from './space/natural/star_view.js';

// natural objects (2026-10-07): the chart names a system's star by its KIND ("orange dwarf", "pulsar", "red giant with a
// white dwarf") and tints its dot to match; with the layer off (atlas:natural, read once a page) the old class and hue.
const NATURAL = naturalOn();
const starLines = new Map<string, string>(), starHexes = new Map<string, number>();
function starLine(sy: StarSystem): string {
  let v = starLines.get(sy.id);
  if (v === undefined) {
    v = `${sy.starClass} star`;
    if (NATURAL) { try { v = systemStarLabel(systemMakeup(sy)); } catch { /* the old line */ } }
    starLines.set(sy.id, v);
  }
  return v;
}
function starHex(sy: StarSystem): number {
  let v = starHexes.get(sy.id);
  if (v === undefined) {
    v = sy.starColor;
    if (NATURAL && sy.id !== 'sys0') { try { v = starChartHex(systemMakeup(sy).primary.kind); } catch { /* the old hue */ } }
    starHexes.set(sy.id, v);
  }
  return v;
}

export interface GalaxyData {
  systems: StarSystem[];
  currentId: string;
  course: { targetId: string; etaS: number } | null;
  /** Sovereignty: systemId → controlling civ (the front line layer). */
  control?: Record<string, 'aurelian' | 'borean' | 'commons'>;
  /** Systems under raider occupation this epoch. */
  incursions?: string[];
  /** Live wormhole pairs (temporary free crossings). */
  wormholes?: Array<{ a: string; b: string }>;
  /** Space Wave 5 (THE INTEL LAW): which systems the pilot has intel on, and how old it is (seconds). Absent = unseen. */
  intelAge?: Record<string, 'fresh' | 'stale'>;
  intelSeenS?: Record<string, number>;
}

const CIV_TINT: Record<string, string> = { aurelian: '#d8b23c', borean: '#4fd0e8' };
const CIV_NAME: Record<string, string> = { aurelian: 'CONCORD space', borean: 'COMPACT space' };
/** A system's sector (±SECTOR_EDGE_M) spans this many chart light-hours when resolved. */
const SYSTEM_SPAN_LH = 2.4;
const hex = (c: number): string => `#${c.toString(16).padStart(6, '0')}`;

interface Mark { id: string; sx: number; sy: number; depth: number; r: number }
interface SysPlanet { name: string; x: number; y: number; z: number; radius: number; color: string }

export class GalaxyMap {
  private root: HTMLDivElement | null = null;
  private canvas: HTMLCanvasElement | null = null;
  private listEl: HTMLDivElement | null = null;
  private zoomEl: HTMLSpanElement | null = null;
  private countEl: HTMLSpanElement | null = null;
  private data: GalaxyData | null = null;
  private raf = 0;
  private selected: string | null = null;
  private hover: string | null = null;
  private filter = '';
  private marks: Mark[] = [];
  private readonly view = new ChartView(0.7, 900, 0.08);
  private dust: Array<[number, number, number, number]> = [];
  private readonly planetCache = new Map<string, SysPlanet[]>();
  private readonly onWarp: (id: string) => void;
  private readonly onCourse: (id: string) => void;
  private readonly onCancelCourse: () => void;
  private readonly onClose: (() => void) | null;
  private readonly onResize = (): void => this.layout();
  // Escape closes THIS chart and nothing else (capture phase beats every bubble-phase Esc menu).
  private readonly onKey = (e: KeyboardEvent): void => {
    if (e.code !== 'Escape' || !this.root) return;
    e.stopImmediatePropagation(); e.preventDefault();
    this.onClose?.();
  };

  constructor(onWarp: (id: string) => void, onCourse: (id: string) => void, onCancelCourse: () => void, onClose: (() => void) | null = null) {
    this.onWarp = onWarp;
    this.onCourse = onCourse;
    this.onCancelCourse = onCancelCourse;
    this.onClose = onClose;
    // Arm dust + core bulge in GALACTIC coordinates (same spiral as galaxy.ts).
    const ARMS = 4, PITCH = 0.32;
    for (let i = 0; i < 1500; i++) {
      const t = hash01(`t${i}`);
      const arm = i % ARMS;
      const theta = t * 4.6 + (arm * Math.PI * 2) / ARMS + (hash01(`j${i}`) - 0.5) * 0.9;
      const r = 90 * Math.exp(PITCH * t * 4.6) * (0.75 + hash01(`r${i}`) * 0.5);
      this.dust.push([Math.cos(theta) * r, (hash01(`y${i}`) - 0.5) * 80 * (1 - t * 0.6), Math.sin(theta) * r, 0.08 + hash01(`a${i}`) * 0.2]);
    }
    for (let i = 0; i < 500; i++) {
      const a = hash01(`ca${i}`) * Math.PI * 2;
      const r = Math.pow(hash01(`cr${i}`), 1.6) * 130;
      this.dust.push([Math.cos(a) * r, (hash01(`cy${i}`) - 0.5) * 60 * (1 - r / 200), Math.sin(a) * r, 0.1 + hash01(`co${i}`) * 0.25]);
    }
  }

  mount(): void {
    if (this.root) return;
    const root = document.createElement('div');
    root.id = 'galaxy-map';
    root.style.cssText =
      'position:fixed;inset:0;z-index:38;display:flex;align-items:center;justify-content:center;gap:18px;' +
      'background:color-mix(in srgb, var(--ax-bg) 88%, transparent);backdrop-filter:blur(3px);font:12px var(--ax-font);color:var(--ax-text);pointer-events:auto';
    const left = document.createElement('div');
    left.style.cssText = 'background:color-mix(in srgb, var(--ax-bg) 92%, transparent);border:1px solid var(--ax-line);border-radius:var(--ax-win-radius);padding:12px';
    const head = document.createElement('div');
    head.style.cssText = 'display:flex;align-items:baseline;gap:10px;margin-bottom:8px';
    const title = document.createElement('span');
    title.textContent = 'GALACTIC CHART';
    title.style.cssText = 'font:700 11px var(--ax-mono);color:var(--ax-muted);letter-spacing:1.4px';
    const count = document.createElement('span');
    count.style.cssText = 'font:11px var(--ax-mono);color:var(--ax-muted);flex:1';
    const zoomLbl = document.createElement('span');
    zoomLbl.style.cssText = 'font:11px var(--ax-mono);color:var(--ax-muted)';
    head.append(title, count, zoomLbl);
    this.countEl = count; this.zoomEl = zoomLbl;
    const cv = document.createElement('canvas');
    cv.style.cssText = 'display:block;cursor:grab;border-radius:var(--ax-radius);background:radial-gradient(circle at 50% 50%,var(--ax-bg2) 0%,var(--ax-bg) 65%,#03060b 100%)';
    this.canvas = cv;
    this.view.attach(cv, {
      onClick: (x, y) => { const m = this.pick(x, y); if (m) { this.selected = m.id; this.renderList(); } },
      onDblClick: (x, y) => { const m = this.pick(x, y); if (m) { this.selected = m.id; this.renderList(); this.view.focus(m.sx, m.sy, 2.2); } else this.view.focus(x, y, 1.8); },
    });
    const bar = document.createElement('div');
    bar.style.cssText = 'display:flex;gap:6px;margin-top:8px;align-items:center';
    const mk = (label: string, tip: string, fn: () => void): HTMLButtonElement => {
      const b = document.createElement('button');
      b.textContent = label; b.title = tip;
      b.style.cssText = 'background:var(--ax-bg3);border:1px solid var(--ax-line);border-radius:var(--ax-radius);color:var(--ax-text);padding:3px 10px;cursor:pointer;font:12px var(--ax-font)';
      b.onclick = fn;
      return b;
    };
    const c = (): number => this.view.size / 2;
    bar.append(
      mk('−', 'Zoom out', () => this.view.zoomAt(c(), c(), 1 / 1.5)),
      mk('+', 'Zoom in', () => this.view.zoomAt(c(), c(), 1.5)),
      mk('◉ here', 'Center on your current system', () => this.focusSystem(this.data?.currentId ?? null, 1)),
      mk('⟲ orbit', 'Toggle the slow auto-orbit', () => { this.view.autoOrbit = !this.view.autoOrbit; }),
      mk('⌂ galaxy', 'Reset to the whole galaxy', () => this.view.reset()),
    );
    const legend = document.createElement('span');
    legend.style.cssText = 'margin-left:auto;font:10px var(--ax-mono);color:var(--ax-muted)';
    legend.textContent = 'drag — orbit · wheel — zoom to a system · right-drag — pan · dbl-click — focus';
    bar.appendChild(legend);
    left.append(head, cv, bar);

    const right = document.createElement('div');
    right.style.cssText = 'background:color-mix(in srgb, var(--ax-bg) 92%, transparent);border:1px solid var(--ax-line);border-radius:var(--ax-win-radius);padding:12px;width:310px;max-height:82vh;display:flex;flex-direction:column';
    const rTitle = document.createElement('div');
    rTitle.textContent = 'SYSTEMS';
    rTitle.style.cssText = 'font:700 11px var(--ax-mono);color:var(--ax-muted);letter-spacing:1.4px;margin-bottom:6px';
    const search = document.createElement('input');
    search.placeholder = NATURAL ? 'find a system, a world or a kind of star…' : 'find a system or world…';
    search.style.cssText = 'width:100%;box-sizing:border-box;background:var(--ax-bg2);border:1px solid var(--ax-line);border-radius:var(--ax-radius);color:var(--ax-text);padding:4px 8px;font:12px var(--ax-font);margin-bottom:8px';
    search.addEventListener('input', () => { this.filter = search.value.trim().toLowerCase(); this.renderList(); });
    search.addEventListener('keydown', (e) => e.stopPropagation());
    const list = document.createElement('div');
    list.style.cssText = 'overflow-y:auto;flex:1';
    right.append(rTitle, search, list);
    const hint = document.createElement('div');
    hint.textContent = 'G / Esc — close · M — system chart · click a star to plot travel · every public world is a system';
    hint.style.cssText = 'position:absolute;bottom:18px;left:50%;transform:translateX(-50%);color:var(--ax-muted)';
    root.append(left, right, hint);
    document.body.appendChild(root);
    this.root = root;
    this.listEl = list;
    this.layout();
    window.addEventListener('resize', this.onResize);
    window.addEventListener('keydown', this.onKey, true);
    const loop = (): void => { this.draw(); this.raf = requestAnimationFrame(loop); };
    this.raf = requestAnimationFrame(loop);
  }

  unmount(): void {
    cancelAnimationFrame(this.raf);
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('keydown', this.onKey, true);
    this.root?.remove();
    this.root = null;
    this.canvas = null;
    this.listEl = null;
  }

  update(d: GalaxyData): void {
    this.data = d;
    if (this.countEl) {
      const worlds = d.systems.filter((s) => s.worldId).length;
      this.countEl.textContent = `${d.systems.length} systems · ${worlds} charted worlds`;
    }
    this.renderList();
  }

  private layout(): void {
    const cv = this.canvas;
    if (!cv) return;
    const size = Math.max(360, Math.min(920, window.innerHeight - 170, window.innerWidth - 440));
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    this.view.size = size;
    cv.style.width = `${size}px`; cv.style.height = `${size}px`;
    cv.width = Math.round(size * dpr); cv.height = Math.round(size * dpr);
  }

  /** px per light-hour at zoom 1: fit the whole galaxy. */
  private scale(): number {
    const d = this.data;
    let maxR = 500;
    if (d) for (const sy of d.systems) maxR = Math.max(maxR, Math.hypot(sy.gx, sy.gz) * 1.06);
    return ((this.view.size / 2 - 24) / maxR) * this.view.zoom;
  }

  private focusSystem(id: string | null, mult: number): void {
    const d = this.data;
    const sys = d?.systems.find((s) => s.id === id);
    if (!sys) return;
    const [sx, sy] = this.view.proj(sys.gx, sys.gy, sys.gz, this.scale());
    this.view.focus(sx, sy, mult);
  }

  private pick(x: number, y: number): Mark | null {
    let best: Mark | null = null, bd = 14 * 14;
    for (const m of this.marks) {
      const dd = (m.sx - x) ** 2 + (m.sy - y) ** 2;
      if (dd < Math.max(bd, (m.r + 5) ** 2) && (best === null || dd < bd)) { bd = dd; best = m; }
    }
    return best;
  }

  /** The in-system bodies of a star, in SECTOR meters (cached per system). */
  private planetsOf(sys: StarSystem): SysPlanet[] {
    let out = this.planetCache.get(sys.id);
    if (out) return out;
    out = [];
    if (sys.id === 'sys0') {
      for (const p of PLANETS) out.push({ name: p.name, x: p.pos[0], y: p.pos[1], z: p.pos[2], radius: p.radius, color: hex(p.rim) });
    } else {
      for (const p of systemSector(sys).planets) out.push({ name: p.name, x: p.pos[0], y: p.pos[1], z: p.pos[2], radius: p.radius, color: `hsl(${Math.round(p.hue * 360)}, 45%, 68%)` });
      if (sys.worldId) {
        try {
          const wp = worldPlanetOf(BigInt(sys.worldId));
          out.push({ name: sys.name.replace(/ System$/, ''), x: wp.x, y: wp.y, z: wp.z, radius: wp.radius, color: `hsl(${Math.round(wp.hue * 360)}, 55%, 70%)` });
        } catch { /* malformed id — the star alone */ }
      }
    }
    this.planetCache.set(sys.id, out);
    return out;
  }

  private renderList(): void {
    const d = this.data;
    const list = this.listEl;
    if (!d || !list) return;
    list.replaceChildren();
    const cur = d.systems.find((s) => s.id === d.currentId);
    if (d.course) {
      const c = document.createElement('div');
      const tgt = d.systems.find((s) => s.id === d.course!.targetId);
      const days = Math.floor(d.course.etaS / 86400);
      const hrs = Math.floor((d.course.etaS % 86400) / 3600);
      const mins = Math.floor((d.course.etaS % 3600) / 60);
      c.style.cssText = 'background:var(--ax-bg3);border:1px solid var(--ax-line2);border-radius:var(--ax-radius);padding:8px;margin-bottom:8px';
      c.textContent = `⛵ EN ROUTE to ${tgt?.name ?? d.course.targetId} — ${days}d ${hrs}h ${mins}m remaining`;
      const cancel = document.createElement('button');
      cancel.textContent = 'Abort course';
      cancel.style.cssText = 'margin-top:6px;background:var(--ax-bg3);border:1px solid color-mix(in srgb, var(--ax-bad) 34%, var(--ax-line));border-radius:var(--ax-radius);color:var(--ax-text);padding:3px 10px;cursor:pointer';
      cancel.onclick = () => this.onCancelCourse();
      c.appendChild(document.createElement('br'));
      c.appendChild(cancel);
      list.appendChild(c);
    }
    const sel = d.systems.find((s) => s.id === this.selected);
    if (sel && cur) {
      const dist = galacticDistance(cur, sel);
      const etaS = dist * COURSE_S_PER_LH;
      const days = (etaS / 86400).toFixed(1);
      const panel = document.createElement('div');
      panel.style.cssText = 'background:var(--ax-bg3);border:1px solid var(--ax-line2);border-radius:var(--ax-radius);padding:8px;margin-bottom:8px';
      const nm = document.createElement('div');
      nm.textContent = `${sel.name} · ${starLine(sel)} · danger ${'☠'.repeat(sel.tier)}`;
      nm.style.cssText = 'font-weight:600;margin-bottom:2px';
      const meta = document.createElement('div');
      const ctl = d.control?.[sel.id];
      const ctlBit = ctl && CIV_NAME[ctl] ? ` · ${CIV_NAME[ctl]}` : '';
      const incBit = d.incursions?.includes(sel.id) ? ' · ☠ INCURSION' : '';
      const ageS = d.intelSeenS?.[sel.id];
      const seenBit = !d.intelAge ? '' : sel.id === d.currentId ? ' · live' : d.intelAge[sel.id] ? ` · seen ${ageS !== undefined ? (ageS < 60 ? 'just now' : ageS < 3600 ? `${Math.floor(ageS / 60)} min ago` : ageS < 86400 ? `${Math.floor(ageS / 3600)} h ago` : `${Math.floor(ageS / 86400)} d ago`) : ''}` : ' · UNSEEN — scout it';
      const bodies = this.planetsOf(sel);
      meta.textContent = `${bodies.length} planets${sel.hasStation ? ' · station' : ''}${sel.worldId ? ' · CHARTED WORLD' : ''}${ctlBit}${incBit}${seenBit}` +
        (sel.id === d.currentId ? ' · YOU ARE HERE' : ` · ${Math.round(dist)} lh out`);
      meta.style.cssText = 'color:var(--ax-soft);font:10px var(--ax-mono);margin-bottom:6px';
      const focus = document.createElement('button');
      focus.textContent = '⌖ Zoom to system';
      focus.style.cssText = 'background:var(--ax-bg3);border:1px solid var(--ax-line2);border-radius:var(--ax-radius);color:var(--ax-soft);padding:4px 10px;cursor:pointer;margin-right:6px;margin-bottom:4px';
      focus.onclick = () => this.focusSystem(sel.id, Math.max(1, 60 / this.view.zoom));
      panel.append(nm, meta, focus);
      if (sel.id !== d.currentId) {
        const warp = document.createElement('button');
        warp.textContent = '⚡ Hyper-warp (30 s tunnel)';
        warp.style.cssText = 'background:var(--ax-bg3);border:1px solid var(--ax-line2);border-radius:var(--ax-radius);color:var(--ax-text);padding:4px 10px;cursor:pointer;margin-right:6px;margin-bottom:4px';
        warp.onclick = () => this.onWarp(sel.id);
        const course = document.createElement('button');
        course.textContent = `⛵ Set course (~${days} d)`;
        course.style.cssText = 'background:var(--ax-bg3);border:1px solid var(--ax-line2);border-radius:var(--ax-radius);color:var(--ax-soft);padding:4px 10px;cursor:pointer;margin-bottom:4px';
        course.onclick = () => this.onCourse(sel.id);
        panel.append(warp, course);
      }
      list.appendChild(panel);
    }
    const f = this.filter;
    const rows = [...d.systems]
      // natural objects: a search also matches the KIND of star ("pulsar", "red giant", "white dwarf") — a star's kind is known from afar
      .filter((s) => !f || s.name.toLowerCase().includes(f) || (NATURAL && starLine(s).toLowerCase().includes(f)))
      .sort((x, y) => (cur ? galacticDistance(cur, x) - galacticDistance(cur, y) : 0))
      .slice(0, f ? 200 : 60);
    for (const sy of rows) {
      const row = document.createElement('div');
      row.style.cssText = `display:flex;gap:6px;padding:3px 6px;border-radius:var(--ax-radius);cursor:pointer;${sy.id === d.currentId ? 'color:var(--ax-good)' : sy.id === this.selected ? 'background:var(--ax-bg3)' : ''}`;
      const nm = document.createElement('span');
      nm.style.cssText = 'flex:1';
      const seen = d.intelAge ? (sy.id === d.currentId ? '' : d.intelAge[sy.id] === 'fresh' ? '' : d.intelAge[sy.id] === 'stale' ? ' ·stale' : ' ·?') : '';
      nm.textContent = `${sy.id === d.currentId ? '◉ ' : ''}${sy.name}${sy.worldId ? ' ✦' : ''}${d.incursions?.includes(sy.id) ? ' ☠' : ''}${seen}`;
      const dd = document.createElement('span');
      dd.style.cssText = 'color:var(--ax-muted);font:10px var(--ax-mono)';
      dd.textContent = cur && sy.id !== cur.id ? `${Math.round(galacticDistance(cur, sy))} lh` : '';
      row.append(nm);
      if (f && NATURAL) {
        const kind = document.createElement('span');
        kind.style.cssText = 'color:var(--ax-soft);font:10px var(--ax-font);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:55%';
        kind.textContent = starLine(sy);
        row.append(kind);
      }
      row.append(dd);
      row.onclick = () => { this.selected = sy.id; this.renderList(); };
      row.ondblclick = () => this.focusSystem(sy.id, Math.max(1, 60 / this.view.zoom));
      list.appendChild(row);
    }
  }

  private draw(): void {
    const cv = this.canvas;
    const g = cv?.getContext('2d');
    const d = this.data;
    if (!cv || !g || !d) return;
    const now = performance.now();
    const v = this.view;
    v.tick(now);
    const S = v.size;
    const dpr = cv.width / S;
    g.setTransform(dpr, 0, 0, dpr, 0, 0);
    g.clearRect(0, 0, S, S);
    const s = this.scale();
    if (this.zoomEl) this.zoomEl.textContent = `zoom ×${v.zoom.toFixed(v.zoom < 3 ? 1 : 0)}`;
    const cur = d.systems.find((x) => x.id === d.currentId);

    // Arm dust + core (only what lands on the canvas).
    g.fillStyle = '#b9cdf0';
    for (const [x, y, z, a] of this.dust) {
      const [sx, sy] = v.proj(x, y, z, s);
      if (sx < -2 || sy < -2 || sx > S + 2 || sy > S + 2) continue;
      g.globalAlpha = a * Math.min(1, 0.6 + v.zoom * 0.2);
      g.fillRect(sx, sy, v.zoom > 8 ? 2 : 1, v.zoom > 8 ? 2 : 1);
    }
    g.globalAlpha = 1;
    const [cx0, cy0] = v.proj(0, 0, 0, s);
    const coreR = Math.min(260, Math.max(30, 140 * s));           // never floods the frame
    const coreA = Math.max(0, 1 - Math.max(0, v.zoom - 6) / 14);  // fades out as a single system takes over
    if (coreA > 0) {
      const core = g.createRadialGradient(cx0, cy0, 0, cx0, cy0, coreR);
      core.addColorStop(0, `rgba(255,236,200,${0.35 * coreA})`); core.addColorStop(0.4, `rgba(255,220,170,${0.12 * coreA})`); core.addColorStop(1, 'rgba(255,220,170,0)');
      g.fillStyle = core;
      g.beginPath(); g.arc(cx0, cy0, coreR, 0, Math.PI * 2); g.fill();
    }

    // Distance rings from HOME (light-hours + manual-course days).
    if (cur) {
      const visibleR = (S / 2) / s;
      const step = niceStep(visibleR, 3);
      g.lineWidth = 1;
      for (let k = 1; k <= 5; k++) {
        const r = step * k;
        if (r * s > S * 1.5) break;
        g.strokeStyle = 'rgba(36,64,94,.45)';
        g.beginPath();
        for (let i = 0; i <= 72; i++) {
          const t = (i / 72) * Math.PI * 2;
          const [sx, sy] = v.proj(cur.gx + Math.cos(t) * r, cur.gy, cur.gz + Math.sin(t) * r, s);
          if (i === 0) g.moveTo(sx, sy); else g.lineTo(sx, sy);
        }
        g.closePath(); g.stroke();
        const [lx, ly] = v.proj(cur.gx + r, cur.gy, cur.gz, s);
        g.fillStyle = 'rgba(111,140,174,.7)';
        g.font = '10px ui-monospace,monospace';
        const days = r * COURSE_S_PER_LH / 86400;
        const dist = r >= 1 ? `${+r.toFixed(1)} lh` : `${Math.round(r * 60)} light-min`;   // no float noise at system scale
        const eta = days >= 0.5 ? `${days.toFixed(1)} d` : `${Math.max(1, Math.round(days * 24))} h`;
        g.fillText(`${dist} · ${eta}`, lx + 4, ly - 3);
      }
    }

    // Wormholes: faint dashed crossings under the stars.
    if (d.wormholes?.length) {
      g.strokeStyle = 'rgba(150,220,255,.4)';
      g.setLineDash([2, 6]);
      for (const w of d.wormholes) {
        const sa = d.systems.find((x) => x.id === w.a);
        const sb = d.systems.find((x) => x.id === w.b);
        if (!sa || !sb) continue;
        const [x1, y1] = v.proj(sa.gx, sa.gy, sa.gz, s);
        const [x2, y2] = v.proj(sb.gx, sb.gy, sb.gz, s);
        g.beginPath(); g.moveTo(x1, y1); g.lineTo(x2, y2); g.stroke();
      }
      g.setLineDash([]);
    }

    // Stars, depth-sorted (far first), with sovereignty tints beneath.
    const zs = Math.sqrt(v.zoom);
    const marks: Mark[] = [];
    const proj = d.systems.map((sy) => {
      const [sx, sy2, depth] = v.proj(sy.gx, sy.gy, sy.gz, s);
      const r = (sy.starClass === 'giant' ? 3.4 : sy.starClass === 'main' ? 2.4 : 1.7) * Math.min(3, Math.max(1, zs * 0.9));
      return { sy, sx, sy2, depth, r };
    }).sort((a, b) => a.depth - b.depth);
    for (const m of proj) {
      const ctl = d.control?.[m.sy.id];
      if (ctl && ctl !== 'commons') {
        g.fillStyle = CIV_TINT[ctl] + '22';
        g.beginPath(); g.arc(m.sx, m.sy2, 10 * Math.pow(v.zoom, 0.35) + m.r, 0, Math.PI * 2); g.fill();
      }
    }
    const systemSpanPx = SYSTEM_SPAN_LH * s;
    let resolved = 0, planetsDrawn = 0;
    const labelRects: Array<[number, number, number, number]> = [];
    const fits = (x: number, y: number, w: number, h: number): boolean => {
      for (const [a, b, c, e] of labelRects) if (x < a + c && x + w > a && y < b + e && y + h > b) return false;
      labelRects.push([x, y, w, h]);
      return true;
    };
    for (const m of proj) {
      const onScreen = m.sx > -40 && m.sy2 > -40 && m.sx < S + 40 && m.sy2 < S + 40;
      if (!onScreen) { marks.push({ id: m.sy.id, sx: m.sx, sy: m.sy2, depth: m.depth, r: m.r }); continue; }
      const col = hex(starHex(m.sy));
      // Resolved system: orbit rings + planets around the star.
      if (systemSpanPx > 36) {
        resolved++;
        const k = SYSTEM_SPAN_LH / SECTOR_EDGE_M;
        g.strokeStyle = 'rgba(200,180,120,.3)';
        g.setLineDash([2, 4]);
        for (const p of this.planetsOf(m.sy)) {
          const orbit = Math.hypot(p.x, p.z) * k;
          if (orbit * s < 6) continue;
          g.beginPath();
          for (let i = 0; i <= 48; i++) {
            const t = (i / 48) * Math.PI * 2;
            const [ox, oy] = v.proj(m.sy.gx + Math.cos(t) * orbit, m.sy.gy + p.y * k, m.sy.gz + Math.sin(t) * orbit, s);
            if (i === 0) g.moveTo(ox, oy); else g.lineTo(ox, oy);
          }
          g.closePath(); g.stroke();
        }
        g.setLineDash([]);
        for (const p of this.planetsOf(m.sy)) {
          const [px, py] = v.proj(m.sy.gx + p.x * k, m.sy.gy + p.y * k, m.sy.gz + p.z * k, s);
          const pr = Math.min(18, Math.max(2, Math.log10(p.radius) * 0.6 * Math.pow(v.zoom, 0.45)));
          if (Math.hypot(px - m.sx, py - m.sy2) < m.r + 2) continue;
          const grad = g.createRadialGradient(px - pr * 0.3, py - pr * 0.3, pr * 0.1, px, py, pr);
          grad.addColorStop(0, '#ffffff'); grad.addColorStop(0.3, p.color); grad.addColorStop(1, '#0a0f18');
          g.fillStyle = grad;
          g.beginPath(); g.arc(px, py, pr, 0, Math.PI * 2); g.fill();
          planetsDrawn++;
          if (systemSpanPx > 160) {
            g.fillStyle = 'rgba(232,220,180,.8)'; g.font = '10px ui-monospace,monospace';
            g.fillText(p.name, px + pr + 3, py + 3);
          }
        }
      }
      // Star glow + disc.
      const glowR = m.r * (m.sy.starClass === 'giant' ? 4 : 3);
      const glow = g.createRadialGradient(m.sx, m.sy2, 0, m.sx, m.sy2, glowR);
      glow.addColorStop(0, col + 'aa'); glow.addColorStop(1, col + '00');
      g.fillStyle = glow;
      g.beginPath(); g.arc(m.sx, m.sy2, glowR, 0, Math.PI * 2); g.fill();
      g.fillStyle = col;
      g.beginPath(); g.arc(m.sx, m.sy2, m.r, 0, Math.PI * 2); g.fill();
      const ctl = d.control?.[m.sy.id];
      if (ctl && ctl !== 'commons') {
        g.strokeStyle = CIV_TINT[ctl];
        g.beginPath(); g.arc(m.sx, m.sy2, m.r + 2.2, 0, Math.PI * 2); g.stroke();
      }
      if (d.incursions?.includes(m.sy.id)) {
        g.strokeStyle = `rgba(255,70,70,${0.55 + Math.sin(now / 220) * 0.35})`;
        g.beginPath(); g.arc(m.sx, m.sy2, m.r + 6.5, 0, Math.PI * 2); g.stroke();
      }
      if (m.sy.worldId) {
        g.strokeStyle = col;
        g.beginPath(); g.arc(m.sx, m.sy2, m.r + 4.2, 0, Math.PI * 2); g.stroke();
        g.fillStyle = col; g.font = '9px ui-monospace,monospace';
        g.fillText('✦', m.sx + m.r + 4, m.sy2 - m.r - 2);
      }
      if (m.sy.id === 'sys0') {
        g.strokeStyle = 'rgba(255,244,224,.6)';
        g.beginPath(); g.arc(m.sx, m.sy2, m.r + 3, 0, Math.PI * 2); g.stroke();
      }
      if (m.sy.id === this.selected) {
        g.strokeStyle = '#ffffff';
        g.beginPath(); g.arc(m.sx, m.sy2, m.r + 5, 0, Math.PI * 2); g.stroke();
      }
      if (m.sy.id === this.hover) {
        g.strokeStyle = 'rgba(255,255,255,.7)';
        g.beginPath(); g.arc(m.sx, m.sy2, m.r + 8, 0, Math.PI * 2); g.stroke();
      }
      if (m.sy.id === d.currentId) {
        const pulse = m.r + 4 + Math.sin(now / 280) * 1.6;
        g.strokeStyle = '#9fe3c0'; g.lineWidth = 1.5;
        g.beginPath(); g.arc(m.sx, m.sy2, pulse, 0, Math.PI * 2); g.stroke();
        g.lineWidth = 1;
      }
      // Labels thin with zoom; important ones always.
      const key = m.sy.id === d.currentId || m.sy.id === this.selected || m.sy.id === this.hover || m.sy.id === 'sys0';
      const want = key || v.zoom >= 5 || (v.zoom >= 1.8 && (m.sy.worldId || m.sy.hasStation));
      if (want) {
        g.font = key ? '600 11px ui-monospace,monospace' : '10px ui-monospace,monospace';
        const w = g.measureText(m.sy.name).width;
        if (key || fits(m.sx + m.r + 4, m.sy2 - 8, w, 12)) {
          g.fillStyle = m.sy.id === d.currentId ? '#9fe3c0' : m.sy.worldId ? 'rgba(226,213,245,.9)' : 'rgba(207,224,245,.8)';
          g.fillText(m.sy.name, m.sx + m.r + 4, m.sy2 + 3);
        }
      }
      marks.push({ id: m.sy.id, sx: m.sx, sy: m.sy2, depth: m.depth, r: m.r });
    }
    this.marks = marks;
    (globalThis as Record<string, unknown>).__galaxyChart = { zoom: v.zoom, resolved, planetsDrawn, selected: this.selected };

    // The active course: dashed line current → target.
    if (d.course && cur) {
      const tgt = d.systems.find((x) => x.id === d.course!.targetId);
      if (tgt) {
        const [x1, y1] = v.proj(cur.gx, cur.gy, cur.gz, s);
        const [x2, y2] = v.proj(tgt.gx, tgt.gy, tgt.gz, s);
        g.strokeStyle = 'rgba(159,227,192,.75)';
        g.setLineDash([4, 5]);
        g.beginPath(); g.moveTo(x1, y1); g.lineTo(x2, y2); g.stroke();
        g.setLineDash([]);
      }
    }

    // Overlays: scale bar + hover card.
    const barLh = niceStep(120 / s, 1);
    const barPx = barLh * s;
    g.strokeStyle = '#9fb8d8'; g.fillStyle = '#9fb8d8';
    g.beginPath(); g.moveTo(14, S - 16); g.lineTo(14 + barPx, S - 16); g.moveTo(14, S - 20); g.lineTo(14, S - 12); g.moveTo(14 + barPx, S - 20); g.lineTo(14 + barPx, S - 12); g.stroke();
    g.font = '10px ui-monospace,monospace';
    g.fillText(barLh >= 1 ? `${barLh} lh` : `${(barLh * 60).toFixed(0)} light-min`, 14, S - 24);
    this.hover = v.mouse.inside ? (this.pick(v.mouse.x, v.mouse.y)?.id ?? null) : null;
    if (this.canvas) this.canvas.style.cursor = this.hover ? 'pointer' : 'grab';
    const hs = this.hover ? d.systems.find((x) => x.id === this.hover) : null;
    if (hs) {
      const m = marks.find((x) => x.id === hs.id)!;
      const ctl = d.control?.[hs.id];
      const line1 = `${hs.name}${hs.worldId ? ' ✦ charted world' : ''}`;
      const line2 = `${starLine(hs)} · ${this.planetsOf(hs).length} planets${hs.hasStation ? ' · station' : ''} · ☠${hs.tier}` +
        (ctl && CIV_NAME[ctl] ? ` · ${CIV_NAME[ctl]}` : '') + (cur && hs.id !== cur.id ? ` · ${Math.round(galacticDistance(cur, hs))} lh` : '');
      g.font = '600 11px system-ui';
      const w = Math.max(g.measureText(line1).width, g.measureText(line2).width) + 16;
      const bx = Math.min(S - w - 6, m.sx + 14), by = Math.max(6, m.sy - 36);
      g.fillStyle = 'rgba(8,13,22,.92)'; g.strokeStyle = '#2c4666';
      g.beginPath(); g.roundRect(bx, by, w, 32, 6); g.fill(); g.stroke();
      g.fillStyle = '#e8f0ff'; g.fillText(line1, bx + 8, by + 13);
      g.fillStyle = '#8fa7c4'; g.font = '10px ui-monospace,monospace'; g.fillText(line2, bx + 8, by + 26);
    }
  }
}
